import React, { useState, useEffect } from 'react';
import { ClipboardList, Inbox, MapPin, Users, Settings, Truck, Navigation } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MainMenu = ({ onNavigate }) => {
  const { user } = useAuth();
  const [openRequestsCount, setOpenRequestsCount] = useState(0);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Helper function to create correct API URL
  const createApiUrl = (endpoint) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    
    if (cleanBaseUrl.endsWith('/api')) {
      const endpointWithoutApi = cleanEndpoint.replace(/^api\//, '');
      return `${cleanBaseUrl}/${endpointWithoutApi}`;
    }
    
    return `${cleanBaseUrl}/${cleanEndpoint}`;
  };

  // Load open requests count
  useEffect(() => {
    const loadOpenRequestsCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoadingRequests(false);
          return;
        }

        const requestsUrl = createApiUrl('api/requests/pending');
        const response = await fetch(requestsUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const requestsData = await response.json();
          setOpenRequestsCount(Array.isArray(requestsData) ? requestsData.length : 0);
        }
      } catch (err) {
        console.error('Error loading open requests count:', err);
      } finally {
        setLoadingRequests(false);
      }
    };

    loadOpenRequestsCount();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadOpenRequestsCount, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    {
      id: 'deal-selection',
      title: 'Besichtigungen & Planung',
      description: 'Umzugsbesichtigungen durchführen und Angebote erstellen',
      icon: ClipboardList,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      step: 'deal-selection'
    },
    {
      id: 'open-requests',
      title: 'Offene Anfragen',
      description: 'Verwalten Sie eingehende Kundenanfragen',
      icon: Inbox,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      step: 'open-requests'
    },
    {
      id: 'routes',
      title: 'Routenplanung',
      description: 'Touren- und Tagesroutenplanung',
      icon: MapPin,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      step: 'route-selection'
    },
    {
      id: 'employee-scheduling',
      title: 'Personalmanagement',
      description: 'Mitarbeiter- und Teameinsatzplanung',
      icon: Users,
      color: 'bg-orange-500',
      hoverColor: 'hover:bg-orange-600',
      step: 'employee-scheduling'
    },
    {
      id: 'fleet-management',
      title: 'Fuhrparkmanagement',
      description: 'Fahrzeuge verwalten und Wartungstermine überwachen',
      icon: Truck,
      color: 'bg-indigo-500',
      hoverColor: 'hover:bg-indigo-600',
      step: 'fleet-management'
    },
    {
      id: 'fleet-live',
      title: 'Live-Standort',
      description: 'Fahrzeuge in Echtzeit auf der Karte verfolgen',
      icon: Navigation,
      color: 'bg-cyan-500',
      hoverColor: 'hover:bg-cyan-600',
      step: 'fleet-live'
    },
    {
      id: 'admin',
      title: 'Administration',
      description: 'Räume, Gegenstände und Einstellungen verwalten',
      icon: Settings,
      color: 'bg-gray-600',
      hoverColor: 'hover:bg-gray-700',
      step: 'admin',
      requiresAdmin: true
    }
  ];

  // Filter menu items based on user role
  const visibleMenuItems = menuItems.filter(item => {
    if (item.requiresAdmin && user?.role !== 'admin') {
      return false;
    }
    return true;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Willkommen im Umzugshelfer</h1>
        <p className="text-neutral-600 text-lg">Wählen Sie ein Modul aus, um zu beginnen</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleMenuItems.map((item) => {
          const IconComponent = item.icon;
          const showBadge = item.id === 'open-requests' && openRequestsCount > 0;
          
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.step)}
              className={`
                group relative bg-white border-2 border-neutral-200 rounded-xl p-6
                hover:border-primary hover:shadow-lg transition-all duration-200
                text-left flex flex-col items-start
                hover:scale-105 transform
              `}
            >
              <div className="relative w-full">
                <div className={`
                  ${item.color} ${item.hoverColor}
                  w-16 h-16 rounded-lg flex items-center justify-center
                  mb-4 transition-colors duration-200
                  group-hover:scale-110 transform
                `}>
                  <IconComponent className="h-8 w-8 text-white" />
                </div>
                
                {/* Badge für offene Anfragen */}
                {showBadge && (
                  <div className="absolute top-0 right-0 bg-red-500 text-white rounded-full min-w-[24px] h-6 px-2 flex items-center justify-center text-xs font-bold shadow-lg animate-pulse">
                    {openRequestsCount > 99 ? '99+' : openRequestsCount}
                  </div>
                )}
              </div>
              
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-primary transition-colors flex items-center gap-2">
                {item.title}
                {item.id === 'open-requests' && loadingRequests && (
                  <span className="text-xs text-neutral-400">(Lädt...)</span>
                )}
              </h3>
              
              <p className="text-neutral-600 text-sm leading-relaxed">
                {item.description}
              </p>

              <div className="mt-4 flex items-center text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                <span>Öffnen</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MainMenu;

